import { IClientConfig, IClientSession, ISessionParams } from 'interfaces/client';
import Api from '@/api/api';
import vModule from 'lib/module';
import langStore from './language';
import socketStore from './socket';
import { ssm, StoreObservable } from './store-manager';
import { toJS } from 'mobx';
import { eventTrack } from '@/lib/event-track';
import moment from 'moment';
import { VAction, VMessageType, VStatus } from '@/interfaces/event-log';
import { AxiosError } from 'axios';

const CLIENT_SESSION_KEY = 'VKYC_CLIENT_SESSION';
const CLIENT_XML_UPLOAD_STATE_KEY = 'CLIENT_XML_UPLOAD_STATE_KEY';

class ClientStore {
  private DEFAULT_CONFIG_IN = {
    start: 'consent',
    modules: {
      consent: {
        next: 'aadhaarXml',
      },
      aadhaarXml: {
        outdated: true,
        next: 'pan',
      },
      pan: {
        panPhoto: {
          ocr: true,
          // enabled: false,
        },
        panInfo: {
          skip: false,
          // enabled: false,
        },
        next: 'check',
      },
      check: {
        geolocation: true,
        media: false,
        next: 'main',
      },
      main: {
        next: '',
      },
    },
  };

  private DEFAULT_CONFIG_ID = {
    start: 'main',
    modules: {
      main: {
        // reserved
        next: '',
      },
    },
  };

  private sessionStore: StoreObservable<IClientSession>;
  private xmlUploadStateKeyStore: StoreObservable<string>;

  constructor() {
    this.sessionStore = ssm.getStoreObservable(CLIENT_SESSION_KEY);
    this.xmlUploadStateKeyStore = ssm.getStoreObservable(CLIENT_XML_UPLOAD_STATE_KEY);
    this.xmlUploadStateKeyStore.setDefault(() => 'PENDING');
  }

  public getSession(): IClientSession | null {
    return this.sessionStore.get();
  }

  public getClientConfig(): IClientConfig {
    return this.sessionStore.getNotNull().config;
  }

  public getXmlUpdateState(): string {
    return this.xmlUploadStateKeyStore.getNotNull();
  }

  public fetchSession = async (params: ISessionParams, isOutbound: boolean) => {
    eventTrack.resetAll();
    if (isOutbound) {
      socketStore.setToOutbound();
    } else {
      socketStore.setToInbound();
    }
    const fetchTime = moment.now();
    try {
      const {
        data: { data },
      } = await Api.session(params);
      eventTrack.pushEvent({
        action: VAction.SESSION,
        startTime: fetchTime,
        endTime: moment.now(),
        status: {
          type: VMessageType.HTTP,
          status: VStatus.SUCCESS,
        },
      });
      if (data) {
        this.updateClientSession(data);
        eventTrack.initUserIdAndTrxId(data.accessToken, params.transactionId);
        langStore.setLan(data.language || '');
        if (!isOutbound) {
          vModule.initModulePathArr(this.getClientConfig().modules);
          vModule.initAndOpen();
        }
      }
    } catch (e) {
      const error: AxiosError = e as any;
      eventTrack.pushEvent({
        action: VAction.SESSION,
        startTime: fetchTime,
        endTime: moment.now(),
        status: {
          type: VMessageType.HTTP,
          status: VStatus.FAILURE,
          errorCode: error.code,
          errorMessage: error.message,
        },
      });
      console.error('error:', e);
      if (isOutbound) {
        socketStore.setStatusToMissed();
      } else {
        socketStore.setStatusToSorry();
      }
      socketStore.goToComplete();
    }
  };

  public cancel = async () => {
    eventTrack.pushEvent({
      action: VAction.CANCEL_CLICK,
    });
    await eventTrack.flushEvents();
    try {
      await Api.cancel();
      if (socketStore.hasVideoStarted()) {
        socketStore.setStatusToThankYou();
      } else {
        socketStore.setStatusToSorry();
      }
      socketStore.goToComplete();
    } catch (e) {
      console.error('error:', e);
    }
  };

  public updateClientSession = (data: IClientSession) => {
    try {
      data.config = this.getInitialClientConfig(data);
      this.sessionStore.set(data);
    } catch (e) {
      throw Error('update client session error');
    }
  };

  public updateXmlUploadState = (data: string) => {
    this.xmlUploadStateKeyStore.set(data);
  };

  public clearClientSession = () => {
    this.sessionStore.clear();
    this.xmlUploadStateKeyStore.clear();
  };

  public getAccessToken = () => {
    return this.getSession()?.accessToken;
  };

  public getTransactionId = () => {
    return this.getSession()?.transactionId;
  };

  public getPrecallTimeout = () => {
    return this.getClientConfig().modules?.precall?.timeout;
  };

  private getInitialClientConfig(session: IClientSession | null): IClientConfig {
    const serverConfig = session?.config;
    const oldPageConfig = session?.pageConfig;
    if (serverConfig?.start && serverConfig?.modules) {
      return {
        start: serverConfig.start,
        modules: serverConfig.modules,
      };
    } else {
      console.log('no latest complete server config found, use default config');
      const startPage = oldPageConfig?.startPage;
      switch (startPage) {
        case 'CONSENT':
          console.log(
            'no latest complete server config found, found old IN config, use default IN config',
          );
          return this.DEFAULT_CONFIG_IN;
        case 'WAITING_ROOM':
          console.log(
            'no latest complete server config found, found old ID config, use default ID config',
          );
          return this.DEFAULT_CONFIG_ID;
        default:
          console.log('no latest and old server config found, please set one');
          throw Error('no server config');
      }
    }
  }
}

const clientStore = new ClientStore();

export default clientStore;

['development', 'staging'].includes(process.env.MODE!) &&
  Object.assign(window, { clientStore, toJS });
